import { Injectable } from '@angular/core';
import { MESSAGES } from 'src/app/core/constants/messages';
import { Line } from 'src/app/core/models/line.model';
import { MatFlexLine } from 'src/app/core/models/materials/mat-flex-line.model';
import { Material } from 'src/app/core/models/materials/material.model';
import { ANNULUS_CONDITION } from 'src/app/core/models/results/annulus-condition';
import { ConnectorFatigueInstructions } from 'src/app/core/models/results/connector/connector-fatigue-instructions.model';
import { ConnectorFatigueLayer } from 'src/app/core/models/results/connector/connector-fatigue-layer.model';
import { ConnectorFatigueStructure } from 'src/app/core/models/results/connector/connector-fatigue-structure.model';
import { ResultConnector } from 'src/app/core/models/results/connector/result-connector.model';
import { LineResultsRequester } from 'src/app/core/models/results/line-results-requester.model';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { FeedbackService } from '../../../feedback/feedback.service';
import { MapsService } from '../../objects/maps/maps.service';
import { MaterialsService } from '../../objects/materials/materials.service';
import { IntervalsService } from '../intervals/intervals.service';

interface MatchingPipes {
  flexible: MatFlexLine[];
}

@Injectable({
  providedIn: 'root',
})
export class ConnectorsService {
  constructor(
    private _intervalsService: IntervalsService,
    private _mapsService: MapsService,
    private _materialsService: MaterialsService,
    private _feedbackService: FeedbackService,
    private _dataSharingService: DataSharingService,
  ) {}

  save(
    lineResultRequester: LineResultsRequester,
    payload: ResultConnector[],
    forceConnectorDeletion = false,
  ): void {
    if (!payload) {
      return;
    }

    const connectorIdsToDelete = new Set<number>();
    const updatedConnectors = payload.map((connector, index) => {
      const { lineId } = lineResultRequester;
      const line: Line = this._mapsService.getObjectById(lineId);
      const matchingPipes = this._intervalsService.getSelectedPipes(
        connector.position,
        connector.position,
        line,
        index,
        true,
        this.connectorVersusSegmentOverlap,
      ) as any;

      if (matchingPipes.flexible.length) {
        connector.fatigueInstructions = this.saveFatigueInstructions(matchingPipes, connector);
      } else {
        connectorIdsToDelete.add(connector.internalId);
      }
      return connector;
    });

    if (IsEqual(lineResultRequester.connectors, updatedConnectors)) {
      return;
    }

    lineResultRequester.connectors = updatedConnectors;

    if (forceConnectorDeletion) {
      this.deleteInBatch(lineResultRequester, connectorIdsToDelete);
    }
    this._dataSharingService.localChanges$.next();
  }
  connectorVersusSegmentOverlap(
    point: number,
    segStart: number,
    segEnd: number,
    intervalIndex: number,
  ): boolean {
    let begIsContainedBySeg = point < segEnd && point >= segStart;
    let endIsContainedBySeg = point > segStart && point <= segEnd;
    const segIsContainedByInterval = point <= segStart && point >= segEnd;

    if (intervalIndex === 0) {
      begIsContainedBySeg = point <= segEnd && point >= segStart;
      endIsContainedBySeg = point > segStart && point <= segEnd;
    }

    return begIsContainedBySeg || endIsContainedBySeg || segIsContainedByInterval;
  }

  deleteInBatch(lineResultRequester: LineResultsRequester, ids: Set<number>): void {
    if (!ids.size) {
      return;
    }

    lineResultRequester.connectors = lineResultRequester.connectors.filter(
      ({ internalId }) => !ids.has(internalId),
    );
    this._feedbackService.warning(MESSAGES.ERROR.CONNECTOR_DELETED_BY_LINE);
  }

  getPresetGlobalValues(connector: ResultConnector): any {
    if (!connector.fatigueInstructions) {
      return null;
    }
    return {
      snCurveId: connector.fatigueInstructions.snCurveId,
      anularCondition: connector.fatigueInstructions.anularCondition,
      stressConcentrationFactorMean: connector.fatigueInstructions.stressConcentrationFactorMean,
      stressConcentrationFactorAlternate:
        connector.fatigueInstructions.stressConcentrationFactorAlternate,
      layerConfiguration: connector.fatigueInstructions.structures[0]?.layerConfiguration,
    };
  }

  saveFatigueInstructions(
    matchingPipes: MatchingPipes,
    connector: ResultConnector,
  ): ConnectorFatigueInstructions {
    const fatigueInstructions = connector.fatigueInstructions;
    const newFadFlex = new ConnectorFatigueInstructions();

    if (fatigueInstructions) {
      Object.assign(newFadFlex, fatigueInstructions);
    }

    const uniquePipes = this.getUniquePipesByCofName(matchingPipes, newFadFlex);
    return this.getUpdatedFadFlex(connector, uniquePipes, fatigueInstructions, newFadFlex);
  }

  getUniquePipesByCofName(
    matchingPipes: MatchingPipes,
    newFadFlex: ConnectorFatigueInstructions,
  ): MatFlexLine[] {
    const uniquePipesByCofName = new Map<string, Material>();
    matchingPipes.flexible.forEach((pipe: MatFlexLine) => {
      uniquePipesByCofName.set(pipe.cofFileName, pipe);
    });
    newFadFlex.structures = [];
    return Array.from(uniquePipesByCofName.values()) as MatFlexLine[];
  }

  resetInstructionsToDefault(instruction: ConnectorFatigueInstructions): void {
    instruction.snCurveId = -1;
    instruction.anularCondition = ANNULUS_CONDITION.DRY;
    instruction.stressConcentrationFactorMean = 1;
    instruction.stressConcentrationFactorAlternate = 1;
    instruction.structures = [];
  }

  getUpdatedFadFlex(
    connector: ResultConnector,
    uniquePipes: Material[],
    prevInstructions: ConnectorFatigueInstructions,
    newFadFlex: ConnectorFatigueInstructions,
  ): ConnectorFatigueInstructions {
    if (!uniquePipes.length) {
      return newFadFlex;
    }

    if (!prevInstructions) {
      prevInstructions = new ConnectorFatigueInstructions();
      connector.fatigueInstructions = prevInstructions;
    }

    if (!prevInstructions) {
      this.resetInstructionsToDefault(newFadFlex);
    }

    const flexPipe = uniquePipes[0] as MatFlexLine;
    const cofWasKept = flexPipe.cofFileName === prevInstructions?.structures[0]?.title;
    const presetData = cofWasKept
      ? this.getDefaultStructure(flexPipe.cofFileName)
      : this.getPresetGlobalValues(connector);
    const oldStructure = cofWasKept ? prevInstructions?.structures?.[0] : presetData;
    let {
      snCurveId,
      anularCondition,
      layerConfiguration: oldLayerConfiguration,
      stressConcentrationFactorMean,
      stressConcentrationFactorAlternate,
    } = oldStructure;
    const layerConfiguration = this.getLayerConfiguration(
      flexPipe,
      cofWasKept,
      oldLayerConfiguration,
    );

    newFadFlex.structures.push(
      new ConnectorFatigueStructure({
        id: null,
        title: flexPipe.cofFileName,
        layerConfiguration,
        snCurveId: cofWasKept ? snCurveId : -1,
        anularCondition: cofWasKept ? anularCondition : ANNULUS_CONDITION.DRY,
        stressConcentrationFactorMean: cofWasKept ? stressConcentrationFactorMean : 1,
        stressConcentrationFactorAlternate: cofWasKept ? stressConcentrationFactorAlternate : 1,
      }),
    );

    newFadFlex.snCurveId = newFadFlex.structures[0].snCurveId;
    newFadFlex.anularCondition = newFadFlex.structures[0].anularCondition;
    newFadFlex.stressConcentrationFactorMean =
      newFadFlex.structures[0].stressConcentrationFactorMean;
    newFadFlex.stressConcentrationFactorAlternate =
      newFadFlex.structures[0].stressConcentrationFactorAlternate;
    return newFadFlex;
  }

  getLayerConfiguration(
    flexPipe: Material,
    cofWasKept: boolean,
    oldLayerConfiguration: ConnectorFatigueLayer[],
  ): ConnectorFatigueLayer[] {
    return this._materialsService.getCofLayers(flexPipe).map(({ name }, layerIndex) => {
      if (cofWasKept && oldLayerConfiguration?.[layerIndex]) {
        return Object.assign(oldLayerConfiguration?.[layerIndex], {
          title: name,
          id: null,
        });
      }
      return this.getDefaultLayer(name);
    });
  }

  getDefaultStructure(title: string): ConnectorFatigueStructure {
    return {
      id: null,
      title,
      snCurveId: -1,
      anularCondition: ANNULUS_CONDITION.DRY,
      stressConcentrationFactorMean: 1,
      stressConcentrationFactorAlternate: 1,
      layerConfiguration: [],
    };
  }

  getDefaultLayer(title: string): ConnectorFatigueLayer {
    return {
      id: null,
      title,
      snCurveId: -1,
      anularCondition: ANNULUS_CONDITION.DRY,
      stressConcentrationFactorMean: 1,
      stressConcentrationFactorAlternate: 1,
    };
  }

  getClosestFlexiblePosition(position: number, line: Line): number {
    let closestFlexiblePosition = 0;
    let currentSegStart = 0;
    let currentSegEnd = 0;

    for (let index = 0; index < line.segmentMesh.length; index++) {
      const length = line.segmentMesh[index].l;
      currentSegEnd = currentSegStart + length;
      const positionFits = this.positionFitsSegment(position, currentSegStart, currentSegEnd);
      const positionIsPast = position < currentSegStart;
      const isFlex = this._intervalsService.checkFlexTypeBySegIndex(index, line);

      if (isFlex) {
        const newClosestRelativePosition = positionIsPast ? currentSegStart : currentSegEnd;
        closestFlexiblePosition = this.getClosestBetweenPositions(position, [
          closestFlexiblePosition,
          newClosestRelativePosition,
        ]);
      }

      if (isFlex && positionFits) {
        return position;
      }

      currentSegStart = currentSegEnd;
    }

    return closestFlexiblePosition;
  }

  getClosestBetweenPositions(position: number, alternatives: number[]): number {
    return alternatives.reduce((acc, curr) => {
      const currentIsCloser = Math.abs(position - curr) < Math.abs(position - acc);
      return currentIsCloser ? curr : acc;
    }, Infinity);
  }

  positionFitsSegment(position: number, segStart: number, segEnd: number): boolean {
    return position >= segStart && position <= segEnd;
  }
}
